/**
 * Träger-User und ihre Freigaben (nur Haupt-Trägeradmin), als Matrix: Zeilen sind die
 * Träger-User, Spalten die Schulen. Die eigene Schule eines Users ist fest angehakt; ein
 * Haken in einer anderen Schule heißt, dass er dort als Schul-Admin arbeiten darf.
 * Wer keinen Haken mehr außerhalb der eigenen Schule hat, verliert den Träger-Zugang.
 */
import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { showToast } from "../utils/toast";
import type { CarrierCandidate, CarrierMember } from "../types";
import { esc } from "./traeger-shared";

let users: CarrierMember[] = [];
let candidates: CarrierCandidate[] = [];
let schools: { id: number; name: string }[] = [];

const ROLE_LABELS: Record<string, string> = {
  "schul-admin": "Schul-Admin", "koordinator": "Koordinator", "viewer": "Betrachter",
};

async function load() {
  const data = await api.carrier.users();
  users = data.users;
  candidates = data.candidates;
  schools = data.schools;
}

export async function render(el: HTMLElement) {
  await load();
  el.innerHTML = `
    <div class="page-header">
      <h1>Träger-User</h1>
      <button class="btn btn-primary" id="tr-add-user">+ Benutzer hinzufügen</button>
    </div>
    <p class="text-muted" style="font-size:.875rem;margin:-.75rem 0 1rem">
      Lege fest, in welchen weiteren Schulen ein Benutzer arbeiten darf. Dort hat er die Rechte eines Schul-Admins.
      In seiner eigenen Schule behält er seine Rolle.
    </p>
    <div class="card table-wrap"><table class="carrier-matrix" id="tr-matrix"></table></div>`;
  renderMatrix(el);

  el.querySelector("#tr-add-user")!.addEventListener("click", () => openAddUser(el));
  el.querySelector("#tr-matrix")!.addEventListener("change", async (e) => {
    const cb = e.target as HTMLInputElement;
    if (!cb.matches("input[data-user]")) return;
    const userId = cb.dataset.user!;
    const user = users.find(u => u.userId === userId);
    if (!user) return;
    const schoolIds = [...el.querySelectorAll<HTMLInputElement>(`input[data-user="${CSS.escape(userId)}"]:not(:disabled)`)]
      .filter(x => x.checked).map(x => Number(x.dataset.school));
    if (schoolIds.length === 0 && !confirm(`${user.name ?? "Der Benutzer"} hat dann keine weitere Schule mehr und verliert den Zugang zum Schulträger. Fortfahren?`)) {
      cb.checked = true;
      return;
    }
    try {
      await api.carrier.setUserSchools(userId, schoolIds);
      await load();
      renderMatrix(el);
      showToast(schoolIds.length === 0 ? "Träger-Zugang entfernt" : "Freigaben gespeichert");
    } catch (err: any) {
      alert("Fehler: " + err.message);
      cb.checked = !cb.checked;
    }
  });
}

function renderMatrix(el: HTMLElement) {
  const table = el.querySelector("#tr-matrix")!;
  const head = `<thead><tr><th>Benutzer</th><th>Eigene Schule</th>${schools.map(s =>
    `<th class="carrier-matrix-school">${esc(s.name)}</th>`).join("")}</tr></thead>`;
  const body = users.map(u => {
    const cells = schools.map(s => {
      const isHome = s.id === u.homeSchoolId;
      // Haupt-Trägeradmin: alle Schulen, nicht änderbar. Eigene Schule: immer.
      if (u.isFounder || isHome) {
        return `<td class="carrier-matrix-cell"><input type="checkbox" checked disabled title="${isHome ? "Eigene Schule" : "Der Haupt-Trägeradmin hat Zugriff auf alle Schulen"}" /></td>`;
      }
      const checked = u.schoolIds.includes(s.id) ? "checked" : "";
      return `<td class="carrier-matrix-cell"><input type="checkbox" data-user="${esc(u.userId)}" data-school="${s.id}" ${checked} /></td>`;
    }).join("");
    return `<tr>
      <td>
        <div style="font-weight:500">${esc(u.name ?? "Unbekannter Benutzer")}${u.isFounder ? ` <span class="badge badge-purple">Haupt-Trägeradmin</span>` : ""}</div>
        <div class="text-muted" style="font-size:.8125rem">${esc(u.email ?? "")}</div>
      </td>
      <td>${esc(u.homeSchoolName ?? "")}</td>
      ${cells}
    </tr>`;
  }).join("");
  table.innerHTML = head + `<tbody>${body}</tbody>`;
}

function openAddUser(el: HTMLElement) {
  if (candidates.length === 0) {
    openModal("Benutzer hinzufügen", `
      <p style="font-size:.875rem;color:var(--gray-600)">Es gibt keine weiteren Benutzer in den Schulen des Schulträgers. Lege Benutzer zuerst in der jeweiligen Schule an.</p>
      <div class="form-actions"><button class="btn btn-primary" id="au-close">OK</button></div>`);
    modalBody().querySelector("#au-close")!.addEventListener("click", closeModal);
    return;
  }
  // Zweistufig: erst die Schule, dann die verfügbaren Benutzer dieser Schule
  const candidateSchools = schools.filter(s => candidates.some(c => c.schoolId === s.id));
  openModal("Benutzer hinzufügen", `
    <div class="form-group"><label>Schule</label><select id="au-school">
      ${candidateSchools.map(s => `<option value="${s.id}">${esc(s.name)}</option>`).join("")}
    </select></div>
    <div class="form-group"><label>Benutzer</label><select id="au-user"></select></div>
    <div class="form-group"><label>Darf zusätzlich arbeiten in</label><div id="au-schools" style="display:flex;flex-direction:column;gap:.375rem"></div></div>
    <div id="au-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="au-cancel">Abbrechen</button><button class="btn btn-primary" id="au-save">Hinzufügen</button></div>`, { wide: true });

  const mb = modalBody();
  const schoolSel = mb.querySelector("#au-school") as HTMLSelectElement;
  const userSel = mb.querySelector("#au-user") as HTMLSelectElement;
  const selectedCandidate = () => candidates.find(c => c.userId === userSel.value && c.schoolId === Number(schoolSel.value));
  const renderUsers = () => {
    const schoolId = Number(schoolSel.value);
    userSel.innerHTML = candidates.filter(c => c.schoolId === schoolId).map(c =>
      `<option value="${esc(c.userId)}">${esc(c.name)} (${esc(c.email)}), ${esc(ROLE_LABELS[c.role] || c.role)}</option>`).join("");
    // Die eigene Schule des Benutzers braucht keine Freigabe
    mb.querySelector("#au-schools")!.innerHTML = schools.filter(s => s.id !== schoolId).map(s => `
      <label style="display:flex;gap:.5rem;align-items:center;font-size:.875rem;font-weight:400">
        <input type="checkbox" value="${s.id}" /> ${esc(s.name)}
      </label>`).join("") || `<p class="text-muted" style="font-size:.875rem;margin:0">Keine weitere Schule vorhanden.</p>`;
  };
  schoolSel.addEventListener("change", renderUsers);
  renderUsers();

  mb.querySelector("#au-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#au-save")!.addEventListener("click", async () => {
    const c = selectedCandidate();
    const errorEl = mb.querySelector("#au-error") as HTMLElement;
    if (!c) { errorEl.textContent = "Bitte einen Benutzer auswählen."; return; }
    const schoolIds = [...mb.querySelectorAll<HTMLInputElement>("#au-schools input:checked")].map(x => Number(x.value));
    if (schoolIds.length === 0) { errorEl.textContent = "Bitte mindestens eine weitere Schule auswählen."; return; }
    try {
      await api.carrier.setUserSchools(c.userId, schoolIds, c.schoolId);
      closeModal();
      await load();
      renderMatrix(el);
      showToast("Träger-User hinzugefügt");
    } catch (err: any) { errorEl.textContent = err.message; }
  });
}
